import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {getError} from 'src/utils/get-error';

import {VALIDATION_ERRORS} from '../constants';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {Error} from 'src/openapi/admin/models/Error';

export const useValidateDomains = () => {
    const {addError, removeError} = useContext<ValidationContextType>(ValidationContext);

    const validateDefaultDomain = useCallback((domains: Array<string>) => {
        let valid = true;

        if (domains.length === 1 && !domains[0]) {
            removeError('account-domain-0');
        } else {
            valid = false;
        }

        return valid;
    }, [removeError]);

    const validateUnfilledDomain = useCallback((domain: string, id: number) => {
        let valid = true;

        if (!domain) {
            addError({
                code: 'DOMAIN_REQUIRED',
                message: 'Domain field is required',
                controlId: `account-domain-${id}`,
            });
            valid = false;
        }

        return valid;
    }, [addError]);

    const validateDuplicatedDomain = useCallback((
        {domains, lastEditedDomain, domain}: {domains: Array<string>, lastEditedDomain: number, domain: string}) => {

        const duplicatedDomains = domains.filter(compare => compare === domain);
        domains.forEach((validationCandidate, id) => {
            if (duplicatedDomains.includes(validationCandidate) &&
                duplicatedDomains.length > 1 &&
                id === lastEditedDomain
            ) {
                addError({
                    code: 'DUPLICATED_DOMAINS',
                    controlId: `account-domain-${lastEditedDomain}`,
                    message: VALIDATION_ERRORS.DUPLICATED_DOMAINS,
                });
            }
        });

        const uniqueDomains = domains.filter(dom => domains.filter(d => dom === d).length < 2);
        domains.forEach((validationCandidate, id) => {
            if (uniqueDomains.includes(validationCandidate)) {
                removeError(`account-domain-${id}`, ['DUPLICATED_DOMAINS']);
            }
        });
    }, [addError, removeError]);

    const validateDomainError = useCallback((props: {error: Error, lastEditedDomain: number}) => {
        const {error, lastEditedDomain} = props;

        addError({
            code: 'DOMAIN_CHECK_ERROR',
            controlId: `account-domain-${lastEditedDomain}`,
            message: getError({error, separator: ' ', errorListOnly: true}),
        });
    }, [addError]);

    return {validateDefaultDomain, validateUnfilledDomain, validateDomainError, validateDuplicatedDomain};
};
